import { ORDER_STATUS_REASON, TASK_STATUS, TASK_STATUS_REASON, type TaskStatus } from '@klareospace/pharos-lib-types';

// ─── Known Codes ──────────────────────────────────────────────────────────────

/**
 * Union of all known order and task status reason codes.
 * Used to distinguish structured codes from legacy free-text strings.
 */
const KNOWN_STATUS_REASON_CODES: ReadonlySet<string> = new Set<string>([
  ...Object.values(ORDER_STATUS_REASON),
  ...Object.values(TASK_STATUS_REASON),
]);

/**
 * Set of task statuses that warrant an error banner.
 * Statuses absent from this set produce no error banner.
 * The i18n key is derived by convention: `drawer.errors.tasks.${task.status}`.
 */
export const TASK_ERROR_STATUSES: ReadonlySet<TaskStatus> = new Set([
  TASK_STATUS.CANCELLED,
  TASK_STATUS.CARRIER_REJECTED,
  TASK_STATUS.FAILED,
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Reason codes whose label resolves via the shared `common:fields.*`; the rest stay local. */
const STATUS_REASON_KEY_OVERRIDES: Partial<Record<string, string>> = {
  [TASK_STATUS_REASON.OTHER]: 'common:fields.other',
};

/**
 * Translates a status reason string for display.
 *
 * - Known code (member of ORDER_STATUS_REASON or TASK_STATUS_REASON): translated via its fully
 *   namespace-qualified key, so `t` may be bound to any namespace.
 * - Unknown non-empty string: returned as-is (legacy free text).
 * - Undefined or empty string: returns undefined.
 */
export function translateStatusReason(t: (key: string) => string, reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  if (KNOWN_STATUS_REASON_CODES.has(reason)) {
    return t(STATUS_REASON_KEY_OVERRIDES[reason] ?? `orders:statusReason.${reason}`);
  }
  return reason;
}

/**
 * Composes a localised error message from a translated prefix and an optional
 * status reason. When a reason is present it is appended after an em-dash
 * separator; otherwise the prefix is returned as-is.
 */
export function composeStatusErrorMessage(
  t: (key: string) => string,
  prefix: string,
  reason: string | undefined,
): string {
  const translatedReason = translateStatusReason(t, reason);
  return translatedReason !== undefined ? `${prefix} — ${translatedReason}` : prefix;
}
